import * as React from 'react';
import * as api from '../services/apiService';
import type { Message, User } from '../types';
import { useAuth } from './AuthProvider';
import { useNavigation } from './NavigationProvider';
import { useUI } from './UIProvider';

const { createContext, useState, useCallback, useMemo, useContext, useEffect } = React;

interface MessagingContextState {
  messages: Message[];
  activeConversationPartner: User | null;
  setActiveConversationPartner: React.Dispatch<React.SetStateAction<User | null>>;
  unreadMessagesCount: number;
  isNewMessageModalOpen: boolean;
  handleSendMessage: (text: string) => Promise<void>;
  handleOpenNewMessageModal: () => void;
  handleCloseNewMessageModal: () => void;
  markMessagesAsReadLocally: (senderHandle: string) => void;
}
const MessagingContext = createContext<MessagingContextState | null>(null);

export const useMessaging = () => {
    const context = useContext(MessagingContext);
    if (!context) throw new Error('useMessaging must be used within a MessagingProvider');
    return context;
};

export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser, authStatus } = useAuth();
    const { view } = useNavigation();
    const { showToast } = useUI();
    const [messages, setMessages] = useState<Message[]>([]);
    const [activeConversationPartner, setActiveConversationPartner] = useState<User | null>(null);
    const [isNewMessageModalOpen, setIsNewMessageModalOpen] = useState(false);
    
    const refreshMessages = useCallback(async () => {
        if (authStatus !== 'authenticated') return;
        try {
            const data = await api.getMessages();
            setMessages(data);
        } catch(e) {
            console.error("Failed to refresh messages", e);
        }
    }, [authStatus]);

    useEffect(() => {
        if (authStatus === 'authenticated' && currentUser && !currentUser.needsOnboarding) {
            refreshMessages();
        }
    }, [authStatus, currentUser, refreshMessages]);
    
    useEffect(() => {
        if (authStatus !== 'authenticated' || !currentUser || currentUser.needsOnboarding) return;
        const pollId = setInterval(refreshMessages, 5000);
        return () => clearInterval(pollId);
    }, [authStatus, currentUser, refreshMessages]);
    
    useEffect(() => {
        if (view !== 'messages' && activeConversationPartner) {
            setActiveConversationPartner(null);
        }
    }, [view, activeConversationPartner]);

    const unreadMessagesCount = useMemo(() => currentUser ? messages.filter(msg => msg.receiverHandle === currentUser.handle && !msg.isRead).length : 0, [messages, currentUser]);

    const handleSendMessage = async (text: string) => {
        if (!currentUser || !activeConversationPartner || !text.trim()) return;
        
        const isFan = currentUser.subscriptionTier === 'fan_basic';
        if (isFan) {
            showToast("Fans cannot send direct messages to the professional network.");
            return;
        }

        await api.sendMessage({ receiverHandle: activeConversationPartner.handle, text: text.trim() });
        await refreshMessages();
    };

    const markMessagesAsReadLocally = useCallback((senderHandle: string) => {
        setMessages(prevMessages => 
            prevMessages.map(msg => 
                (msg.senderHandle === senderHandle && !msg.isRead) 
                ? { ...msg, isRead: true } 
                : msg
            )
        );
    }, []);

    const handleOpenNewMessageModal = () => {
        const isFan = currentUser?.subscriptionTier === 'fan_basic';
        if (isFan) {
            showToast("Fan accounts are restricted to observer mode and cannot initiate chats.");
            return;
        }
        setIsNewMessageModalOpen(true);
    };

    const handleCloseNewMessageModal = () => setIsNewMessageModalOpen(false);
    
    const value = { 
        messages, 
        activeConversationPartner, 
        setActiveConversationPartner, 
        unreadMessagesCount, 
        isNewMessageModalOpen, 
        handleSendMessage, 
        handleOpenNewMessageModal, 
        handleCloseNewMessageModal,
        markMessagesAsReadLocally
    };

    return (
        <MessagingContext.Provider value={value}>
            {children}
        </MessagingContext.Provider>
    );
};